import { apiClient } from './api';
import type { 
  Set, 
  CreateSetInput, 
  UpdateSetInput, 
  ApiCollection 
} from './types';

export class SetService {
  private static readonly ENDPOINT = '/api/sets';

  static async getAll(): Promise<Set[]> {
    const response = await apiClient.get<ApiCollection<Set>>(this.ENDPOINT);
    return response.data['member'];
  }

  static async getById(id: number): Promise<Set> {
    const response = await apiClient.get<Set>(`${this.ENDPOINT}/${id}`);
    return response.data;
  }

  static async create(set: CreateSetInput): Promise<Set> {
    const response = await apiClient.post<Set>(this.ENDPOINT, set);
    return response.data;
  }

  static async update(id: number, set: Partial<CreateSetInput>): Promise<Set> {
    const response = await apiClient.patch<Set>(`${this.ENDPOINT}/${id}`, set);
    return response.data;
  }

  static async delete(id: number): Promise<void> {
    await apiClient.delete(`${this.ENDPOINT}/${id}`);
  }

  // Utility methods
  static async getByWorkoutSession(workoutSessionId: number): Promise<Set[]> {
    const sets = await this.getAll();
    return sets.filter(set => 
      set.workoutSession === `/api/workout_sessions/${workoutSessionId}`
    );
  }

  static async getByExercise(exerciseId: number): Promise<Set[]> {
    const sets = await this.getAll();
    return sets.filter(set => 
      set.exercise === `/api/exercises/${exerciseId}`
    );
  }

  static async getByWorkoutSessionAndExercise(workoutSessionId: number, exerciseId: number): Promise<Set[]> {
    const sets = await this.getAll();
    return sets.filter(set => 
      set.workoutSession === `/api/workout_sessions/${workoutSessionId}` &&
      set.exercise === `/api/exercises/${exerciseId}`
    );
  }

  // Bulk operations for creating multiple sets
  static async createMultiple(sets: CreateSetInput[]): Promise<Set[]> {
    const createdSets: Set[] = [];
    for (const setData of sets) {
      const createdSet = await this.create(setData);
      createdSets.push(createdSet);
    }
    return createdSets;
  }

  // Helper for reordering sets
  static async reorderSets(setIds: number[], newOrder: number[]): Promise<Set[]> {
    const updatedSets: Set[] = [];
    for (let i = 0; i < setIds.length; i++) {
      const updatedSet = await this.update(setIds[i], { setOrder: newOrder[i] });
      updatedSets.push(updatedSet);
    }
    return updatedSets;
  }
}

export default SetService;